import {
  Children,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type TransitionEvent,
} from 'react';
import { cx } from '@wapharma/design-system';
import { stepTravelDir } from './stepTravelDir';
import styles from './StepSwitch.module.css';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Content height — prefer scrollHeight so flex-stretched boxes don't inflate the tween. */
function measureContentHeight(panel: HTMLDivElement | null): number {
  if (!panel) return 0;
  const byScroll = Math.ceil(panel.scrollHeight);
  const byRect = Math.ceil(panel.getBoundingClientRect().height);
  // When idle/single panel, scrollHeight tracks content; during dual-panel slides rect is fine.
  return Math.max(byScroll, 0) || byRect;
}

export type StepSwitchProps = {
  index: number;
  children: ReactNode;
  className?: string;
  /**
   * Stretch inside a flex sheet body (Configuration initiale fill steps).
   * Skips height tween — translate only.
   */
  fill?: boolean;
  /**
   * `panels` — each child is a permanently mounted panel (login).
   * `snapshot` — single swapping child; previous tree slides out (drawers).
   */
  mode?: 'panels' | 'snapshot';
  onTransitionEnd?: () => void;
};

type SnapshotPhase = 'idle' | 'prepare' | 'animating';

/**
 * Horizontal step carousel. Stable outer container; clipped viewport;
 * full-width slide + height tween (login email/password physics).
 */
export function StepSwitch({
  index,
  children,
  className,
  fill = false,
  mode = 'snapshot',
  onTransitionEnd,
}: StepSwitchProps) {
  if (mode === 'panels') {
    return (
      <StepSwitchPanels
        index={index}
        className={className}
        fill={fill}
        onTransitionEnd={onTransitionEnd}
      >
        {children}
      </StepSwitchPanels>
    );
  }
  return (
    <StepSwitchSnapshot
      index={index}
      className={className}
      fill={fill}
      onTransitionEnd={onTransitionEnd}
    >
      {children}
    </StepSwitchSnapshot>
  );
}

function StepSwitchPanels({
  index,
  children,
  className,
  fill,
  onTransitionEnd,
}: Omit<StepSwitchProps, 'mode'>) {
  const panels = Children.toArray(children);
  const panelCount = Math.max(1, panels.length);
  const safeIndex = Math.min(Math.max(0, index), panelCount - 1);

  const viewportRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [slideWidth, setSlideWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [animReady, setAnimReady] = useState(false);
  const [sliding, setSliding] = useState(false);
  const [trackedIndex, setTrackedIndex] = useState(safeIndex);

  // Sync index → sliding in render so first paint still has transform (no jump).
  if (trackedIndex !== safeIndex) {
    setTrackedIndex(safeIndex);
    if (animReady && !prefersReducedMotion()) setSliding(true);
  }

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const sync = () => {
      const next = Math.round(viewport.clientWidth);
      setSlideWidth((prev) => (prev === next ? prev : next));
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(viewport);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (fill) return;
    const sync = () => {
      const next = measureContentHeight(panelRefs.current[safeIndex] ?? null);
      if (next > 0) setViewportHeight((prev) => (prev === next ? prev : next));
    };
    sync();
    const el = panelRefs.current[safeIndex];
    if (!el) return;
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);
    return () => ro.disconnect();
  }, [safeIndex, fill, children]);

  const handleTrackTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'transform') return;
    if (event.target !== event.currentTarget) return;
    setSliding(false);
    onTransitionEnd?.();
  };

  // Idle on panel 0: no transform (crisp). Else translateX — never idle translate3d(0,0,0).
  const offsetPx = slideWidth > 0 ? safeIndex * slideWidth : 0;
  const atRestOrigin = safeIndex === 0 && !sliding;
  const slideTransform = atRestOrigin
    ? undefined
    : slideWidth > 0
      ? `translateX(-${offsetPx}px)`
      : `translateX(-${(safeIndex / panelCount) * 100}%)`;

  const viewportStyle = {
    ...(slideWidth > 0
      ? {
          '--wp-step-slide-width': `${slideWidth}px`,
          '--wp-step-panel-count': String(panelCount),
        }
      : { '--wp-step-panel-count': String(panelCount) }),
    ...(!fill && viewportHeight != null && viewportHeight > 0
      ? { height: viewportHeight }
      : {}),
  } as CSSProperties;

  return (
    <div
      ref={viewportRef}
      className={cx(
        styles.root,
        (sliding || safeIndex > 0) && styles.rootClipping,
        fill && styles.fill,
        slideWidth > 0 && styles.rootMeasured,
        animReady && !fill && styles.rootAnimated,
        className,
      )}
      style={viewportStyle}
    >
      <div
        className={cx(
          styles.track,
          sliding && styles.trackSliding,
          animReady && styles.trackAnimated,
        )}
        style={{
          ...(slideTransform ? { transform: slideTransform } : {}),
          width: slideWidth > 0 ? undefined : `${panelCount * 100}%`,
        }}
        onTransitionEnd={handleTrackTransitionEnd}
      >
        {panels.map((panel, i) => {
          const active = i === safeIndex;
          return (
            <div
              key={i}
              ref={(node) => {
                panelRefs.current[i] = node;
              }}
              className={styles.panel}
              data-active={active}
              style={
                slideWidth > 0
                  ? undefined
                  : { flex: `0 0 ${100 / panelCount}%`, width: `${100 / panelCount}%` }
              }
              {...(!active ? { inert: true, 'aria-hidden': true } : {})}
            >
              {panel}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepSwitchSnapshot({
  index,
  children,
  className,
  fill,
  onTransitionEnd,
}: Omit<StepSwitchProps, 'mode'>) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const enterRef = useRef<HTMLDivElement>(null);
  const leaveRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef(children);
  const prevIndexRef = useRef(index);

  const [leaveNode, setLeaveNode] = useState<ReactNode>(null);
  const [dir, setDir] = useState<1 | -1>(1);
  const [phase, setPhase] = useState<SnapshotPhase>('idle');
  const [slideWidth, setSlideWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [animReady, setAnimReady] = useState(false);
  /** Sheet width before and after this slide, when those widths differ. */
  const [travel, setTravel] = useState<{ from: number; to: number } | null>(null);
  const restWidthRef = useRef(0);
  const phaseRef = useRef<SnapshotPhase>('idle');

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  phaseRef.current = phase;

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const sync = () => {
      if (phaseRef.current !== 'idle') return;
      const next = Math.round(viewport.clientWidth);
      if (next <= 0) return;
      restWidthRef.current = next;
      setSlideWidth((prev) => (prev === next ? prev : next));
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(viewport);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const prevIndex = prevIndexRef.current;
    const nextDir = stepTravelDir(prevIndex, index);
    if (nextDir === 0) {
      liveRef.current = children;
      return;
    }
    if (prefersReducedMotion()) {
      prevIndexRef.current = index;
      liveRef.current = children;
      setLeaveNode(null);
      setTravel(null);
      setPhase('idle');
      onTransitionEnd?.();
      return;
    }
    const viewport = viewportRef.current;
    const toWidth = viewport ? Math.round(viewport.clientWidth) : 0;
    const fromWidth = restWidthRef.current || toWidth;
    if (fromWidth > 0 && toWidth > 0 && fromWidth !== toWidth) {
      setTravel({ from: fromWidth, to: toWidth });
    } else {
      setTravel(null);
    }
    if (toWidth > 0) setSlideWidth((prev) => (prev === toWidth ? prev : toWidth));
    setLeaveNode(liveRef.current);
    setDir(nextDir);
    setPhase('prepare');
    prevIndexRef.current = index;
    liveRef.current = children;
  }, [index, children, onTransitionEnd]);

  useLayoutEffect(() => {
    if (phase !== 'prepare') return;
    if (fill) {
      const raf = requestAnimationFrame(() => setPhase('animating'));
      return () => cancelAnimationFrame(raf);
    }
    // One-shot height lock (no CSS height tween). Leave → enter on next frame.
    const leaveH = measureContentHeight(leaveRef.current);
    const enterH = measureContentHeight(enterRef.current);
    if (leaveH > 0) setViewportHeight(leaveH);
    else if (enterH > 0) setViewportHeight(enterH);
    const raf = requestAnimationFrame(() => {
      if (enterH > 0) setViewportHeight(enterH);
      setPhase('animating');
    });
    return () => cancelAnimationFrame(raf);
  }, [phase, fill]);

  // Enter content often grows mid-slide (async Tags families). RO corrects height
  // with discrete writes — not a 380ms layout tween.
  useLayoutEffect(() => {
    if (fill || phase !== 'animating') return;
    const el = enterRef.current;
    if (!el) return;
    const sync = () => {
      const next = measureContentHeight(el);
      if (next > 0) setViewportHeight((prev) => (prev === next ? prev : next));
    };
    sync();
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase, fill, children]);

  useLayoutEffect(() => {
    if (phase !== 'idle' || fill) return;
    const el = enterRef.current;
    if (!el) {
      setViewportHeight(null);
      return;
    }
    // Settle: keep height lock while async content still grows, then release to auto
    // (locked height clips Input focus rings into Paper padding).
    let settleTimer: number | null = null;
    const releaseSoon = () => {
      if (settleTimer != null) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => setViewportHeight(null), 120);
    };
    const sync = () => {
      const next = measureContentHeight(el);
      if (next > 0) setViewportHeight((prev) => (prev === next ? prev : next));
      releaseSoon();
    };
    sync();
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (settleTimer != null) window.clearTimeout(settleTimer);
    };
  }, [phase, fill, children]);

  useLayoutEffect(() => {
    if (!leaveNode || phase !== 'animating') return;
    if (prefersReducedMotion()) {
      setLeaveNode(null);
      setTravel(null);
      setPhase('idle');
      return;
    }
    const t = window.setTimeout(() => {
      const viewport = viewportRef.current;
      const next = viewport ? Math.round(viewport.clientWidth) : 0;
      if (next > 0) restWidthRef.current = next;
      setLeaveNode(null);
      setTravel(null);
      setPhase('idle');
      onTransitionEnd?.();
    }, 450);
    return () => window.clearTimeout(t);
  }, [leaveNode, phase, onTransitionEnd]);

  const handleTrackTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'transform' && event.propertyName !== 'width') return;
    if (event.target !== event.currentTarget && event.target !== viewportRef.current) return;
    if (phase !== 'animating') return;
    const viewport = viewportRef.current;
    const next = viewport ? Math.round(viewport.clientWidth) : 0;
    if (next > 0) restWidthRef.current = next;
    setLeaveNode(null);
    setTravel(null);
    setPhase('idle');
    onTransitionEnd?.();
  };

  const leaving = leaveNode != null;
  const split =
    !fill &&
    leaving &&
    travel != null &&
    travel.from > 0 &&
    travel.to > 0 &&
    travel.from !== travel.to;
  // Forward (dir=1): leave left, enter right; track 0 → -width
  // Back (dir=-1): enter left, leave right; track -width → 0
  // Split: each panel keeps its own width so a wider sheet does not squash the incoming step.
  const trackOffset =
    !leaving || phase === 'idle'
      ? 0
      : split
        ? dir === 1
          ? phase === 'prepare'
            ? 0
            : -travel.from
          : phase === 'prepare'
            ? -travel.to
            : 0
        : dir === 1
          ? phase === 'prepare'
            ? 0
            : -(slideWidth || 0)
          : phase === 'prepare'
            ? -(slideWidth || 0)
            : 0;

  // Idle: no transform (crisp text). Sliding: translateX only — no translate3d / will-change idle.
  const slideTransform = !leaving
    ? undefined
    : slideWidth > 0
      ? `translateX(${trackOffset}px)`
      : dir === 1
        ? phase === 'animating'
          ? 'translateX(-50%)'
          : 'translateX(0)'
        : phase === 'animating'
          ? 'translateX(0)'
          : 'translateX(-50%)';

  // Height lock only while sliding (tween leave → enter). Idle = auto so Paper pad breathes.
  const viewportStyle = {
    ...(split
      ? { width: phase === 'animating' ? travel.to : travel.from }
      : leaving && slideWidth > 0
        ? {
            '--wp-step-slide-width': `${slideWidth}px`,
            '--wp-step-panel-count': '2',
          }
        : slideWidth > 0
          ? {
              '--wp-step-slide-width': `${slideWidth}px`,
              '--wp-step-panel-count': '1',
            }
          : {}),
    // Height lock while sliding OR brief idle settle (async enter growth).
    ...(!fill && viewportHeight != null && viewportHeight > 0
      ? { height: viewportHeight }
      : {}),
  } as CSSProperties;

  const enterPanel = (
    <div
      ref={enterRef}
      className={styles.panel}
      data-active={true}
      style={
        split
          ? { flex: `0 0 ${travel.to}px`, width: travel.to }
          : leaving && slideWidth > 0
            ? undefined
            : { flex: '0 0 100%', width: '100%' }
      }
    >
      {children}
    </div>
  );

  const leavePanel = leaving ? (
    <div
      ref={leaveRef}
      className={styles.panel}
      data-active={false}
      aria-hidden
      inert
      style={split ? { flex: `0 0 ${travel.from}px`, width: travel.from } : undefined}
    >
      {leaveNode}
    </div>
  ) : null;

  const trackChildren = !leaving
    ? enterPanel
    : dir === 1
      ? (
          <>
            {leavePanel}
            {enterPanel}
          </>
        )
      : (
          <>
            {enterPanel}
            {leavePanel}
          </>
        );

  return (
    <div
      ref={viewportRef}
      className={cx(
        styles.root,
        leaving && styles.rootClipping,
        fill && styles.fill,
        slideWidth > 0 && leaving && !split && styles.rootMeasured,
        split && styles.rootSplit,
        split && phase === 'animating' && styles.rootWidthTravel,
        animReady && !fill && styles.rootAnimated,
        className,
      )}
      style={viewportStyle}
    >
      <div
        className={cx(
          styles.track,
          leaving && styles.trackSliding,
          animReady && leaving && phase === 'animating' && styles.trackAnimated,
        )}
        style={{
          ...(slideTransform ? { transform: slideTransform } : {}),
          width: split
            ? travel.from + travel.to
            : leaving
              ? slideWidth > 0
                ? undefined
                : '200%'
              : '100%',
        }}
        onTransitionEnd={handleTrackTransitionEnd}
      >
        {trackChildren}
      </div>
    </div>
  );
}
